import * as THREE from 'three';
import { ClipTask } from './defines';
import { Box3Helper } from './utils/Box3Helper';
import { _pointcloudTransformVersion, pointBudget, maxNodesLoading, loaded_tiles_network, rendered_tiles, stats, resetStats } from './potreeState';
const lru = () => window.Potree?.lru;

interface ClipBox {
  box: THREE.Object3D & { matrixWorld: THREE.Matrix4; getActualClipTask(): number };
}

interface ClipPlaneObject extends THREE.Object3D {
  actualClipTask: number;
  heatmapIsolate?: boolean;
  getNormal(): THREE.Vector3;
  tolerance: number;
  pointInsideClipArea(point: THREE.Vector3): boolean;
}

interface ClipPlane {
  plane: ClipPlaneObject;
}

interface PointCloudMaterial {
  clipBoxes: ClipBox[];
  clipPolygons: unknown[];
  clipPlanes: ClipPlane[];
  activeAttributeName: string | null;
  numClipBoxes: number;
  numClipPolygons: number;
  numSnapshots: number;
  visibleNodesTexture: THREE.Texture;
  [key: string]: unknown;
}

interface SceneNode extends THREE.Object3D {
  updateMatrix(): void;
  matrix: THREE.Matrix4;
  matrixWorld: THREE.Matrix4;
  material: PointCloudMaterial;
  visible: boolean;
}

interface Node {
  boundingBoxNode?: THREE.Object3D;
  geometryNode?: { id: string | number; loaded: boolean; numPoints: number; spacing?: number; children: Record<string, unknown> };
  getBoundingBox(): THREE.Box3;
  getBoundingSphere(): THREE.Sphere;
  getChildren(): Node[];
  getLevel(): number;
  getNumPoints(): number;
  isGeometryNode(): boolean;
  isLoaded(): boolean;
  isTreeNode(): boolean;
  load(): void;
  preLoad?(): void;
  name: string;
  numCandidateChildren: number;
  numInvisibleChildren: number;
  numVisibleChildren: number;
  refinedByAllChildren?: boolean;
  sceneNode: SceneNode;
  skipDuplicatePointsPythagoras?: boolean;
  spacing?: number;
  _transformVersion?: number;
}

interface PointCloud {
  boundingBoxNodes: THREE.Object3D[];
  deepestVisibleLevel: number;
  dem?: { update(nodes: Node[]): void };
  dispatchEvent(event: Record<string, unknown>): void;
  downSamplingLevel: number;
  generateDEM?: boolean;
  hideDescendants(root: Node | SceneNode): void;
  initialized(): boolean;
  material: PointCloudMaterial;
  matrixWorld: THREE.Matrix4;
  maxLevel: number;
  minimumNodePixelSize: number;
  numRefinedParents: number;
  numSkippedPoints: number;
  numVisibleNodes: number;
  numVisiblePoints: number;
  pcoGeometry: {
    boundingBox: THREE.Box3;
    spacing: number;
    type?: string;
    root?: Node;
    nodes: Record<string, Node>;
  };
  pointBudget: number;
  root: Node;
  showBoundingBox: boolean;
  toTreeNode(geometryNode: Node, parent?: Node): Node;
  updateMaterial(material: PointCloudMaterial, visibleNodes: Node[], camera: Camera, renderer: THREE.WebGLRenderer): void;
  updateMatrixWorld(force?: boolean): void;
  updateVisibleBounds(): void;
  visible: boolean;
  visibleGeometry: Node[];
  visibleNodes: Node[];
}

type Camera = THREE.PerspectiveCamera | THREE.OrthographicCamera;

/**
 * One camera contributing to a multi-camera visibility pass.
 * domHeight drives the screen-space pixel weight, so each view's canvas
 * gets its own LOD pressure independent of the others.
 */
export interface VisibilityCamera {
  camera: Camera;
  domHeight: number;
}

/** Element stored in the BinaryHeap priority queue during visibility update */
interface PriorityQueueElement {
  pointcloud: number;
  node: Node;
  parent?: Node;
  weight: number;
}

let endRender: number | undefined;

// Granular profiling for updatePointClouds - exposed globally for debug widget
export const updatePointCloudsProfile = {
  updateVisibility: 0,
  updateMaterial: 0,
  updateVisibleBounds: 0,
  freeMemory: 0,
};

/**
 * Per-widget extra point budget (added on top of primary's full pointBudget).
 * Widget pass 2 can only push this many additional points into visibleNodes
 * beyond what primary already used.
 */
const WIDGET_EXTRA_BUDGET_FRACTION = 0.1;

/**
 * Run the visibility/LOD pass against one or more cameras.
 *
 * Strategy: two passes when widgets are present.
 *  1. Primary pass — runs the LOD loop with cameras[0] only, against the full
 *     pointBudget. Resets state and populates pointcloud.visibleNodes.
 *  2. Widget pass — runs the loop again with cameras[1..], with state kept,
 *     and an extra per-widget budget on top. Nodes already marked visible by
 *     primary are skipped (no double-bill); widget-only nodes can be added up
 *     to the extra cap.
 *
 * This keeps primary's LOD totally independent of whether widgets are open,
 * fixing the "open widget → main view unloads" bug.
 *
 * The first camera in `cameras` is the "primary": it's passed to
 * `pointcloud.updateMaterial` (which uses one camera for screen-space material
 * heuristics).
 */
/**
 * Nodes the LAST visibility pass wanted to show but could not yet — either
 * their geometry is not loaded or it awaits its GPU upload slot. 0 means the
 * LOD is fully settled for the current cameras/budget/clips: the traversal
 * has nothing left to fetch. Consumers (e.g. AutoFloorTool's focused-load
 * wait) poll this per frame as the authoritative "everything loaded" signal.
 */
export let lastFramePendingNodes = 0;

/**
 * World-space LOD focus region. While set, nodes intersecting it traverse
 * CAMERA-INDEPENDENTLY: the frustum test and the minimumNodePixelSize gate
 * are bypassed and children are weighted by object-space size (coarse
 * first), so the region refines to the full point budget no matter where
 * the camera looks or how far away it is. Used by AutoFloorTool's focused
 * load; the point budget and maxLevel/downSamplingLevel caps still apply.
 */
let lodFocusBox: THREE.Box3 | null = null;

export function setLodFocusBox(box: THREE.Box3 | null): void {
  lodFocusBox = box ? box.clone() : null;
}

export function updatePointClouds(pointclouds: PointCloud[], cameras: VisibilityCamera[], renderer: THREE.WebGLRenderer) {
  if (cameras.length === 0) {
    return { lowestSpacing: Infinity };
  }
  // Clear protected set before visibility pass — it will be repopulated by lru.touch()
  lru()?.protected.clear();

  // Profile counters accumulate across passes within a single updatePointClouds
  // call; reset here so cross-pass totals are correctly reported per frame.
  resetVisibilityProfile();

  const updateVisibilityStart = performance.now();
  // Per-frame shared state. unloadedGeometry, GPU-upload count, and
  // lowestSpacing all need to be accumulated across passes so that per-frame
  // caps (maxNodesLoading, 2 GPU uploads) and metrics aren't double-counted.
  const passState = createVisibilityPassState();

  // Pass 1: primary camera only, full point budget, normal reset behavior.
  updateVisibility(pointclouds, [cameras[0]], renderer, { passState });

  // Pass 2: widget cameras (if any), no state reset, extra budget on top.
  if (cameras.length > 1) {
    const widgetCameras = cameras.slice(1);
    const extraBudget = Math.floor(pointBudget * WIDGET_EXTRA_BUDGET_FRACTION) * widgetCameras.length;
    updateVisibility(pointclouds, widgetCameras, renderer, { resetState: false, extraBudget, passState });
  }
  updatePointCloudsProfile.updateVisibility = performance.now() - updateVisibilityStart;

  // Load nodes after BOTH passes so the per-frame maxNodesLoading cap is the
  // global per-frame cap — running the loader inside each pass would double
  // it and saturate the network/decode pipeline.
  const nodeLoadStart = performance.now();
  const toLoad = Math.min(maxNodesLoading, passState.unloadedGeometry.length);
  for (let i = 0; i < toLoad; i++) {
    passState.unloadedGeometry[i].load();
  }
  updateVisibilityProfile.nodeLoad = performance.now() - nodeLoadStart;
  lastFramePendingNodes = passState.unloadedGeometry.length;

  const result = { lowestSpacing: passState.lowestSpacing.value };

  const primaryCamera = cameras[0].camera;
  const updateMaterialStart = performance.now();
  for (const pointcloud of pointclouds) {
    pointcloud.updateMaterial(pointcloud.material, pointcloud.visibleNodes, primaryCamera, renderer);
  }
  updatePointCloudsProfile.updateMaterial = performance.now() - updateMaterialStart;

  const updateVisibleBoundsStart = performance.now();
  for (const pointcloud of pointclouds) {
    pointcloud.updateVisibleBounds();
  }
  updatePointCloudsProfile.updateVisibleBounds = performance.now() - updateVisibleBoundsStart;

  const freeMemoryStart = performance.now();
  // Pythagoras - Free memory from LRU cache when exceeding point budget
  lru()?.freeMemory();
  updatePointCloudsProfile.freeMemory = performance.now() - freeMemoryStart;

  return result;
}

export function updateVisibilityStructures(pointclouds: PointCloud[], cameras: VisibilityCamera[], resetState = true) {
  // For each pointcloud, build one frustum and one camera-object-position per camera.
  // resetState=false skips clearing per-pointcloud visibility / hideDescendants so
  // a second pass (e.g. widget-only) can extend the primary pass's results
  // instead of replacing them.
  const frustumsByPC: THREE.Frustum[][] = [];
  const camObjPositionsByPC: THREE.Vector3[][] = [];
  const priorityQueue = new BinaryHeap<PriorityQueueElement>(function (x) {
    return 1 / x.weight;
  });

  // Pre-update camera matrices once; repeated updates inside the loop would be wasteful.
  for (const vc of cameras) {
    vc.camera.updateMatrixWorld();
    vc.camera.updateProjectionMatrix();
  }

  for (let i = 0; i < pointclouds.length; i++) {
    const pointcloud = pointclouds[i];

    if (!pointcloud.initialized()) {
      frustumsByPC.push([]);
      camObjPositionsByPC.push([]);
      continue;
    }

    if (resetState) {
      pointcloud.numVisibleNodes = 0;
      pointcloud.numVisiblePoints = 0;

      pointcloud.numRefinedParents = 0;
      pointcloud.numSkippedPoints = 0;

      pointcloud.deepestVisibleLevel = 0;
      pointcloud.visibleNodes = [];
      pointcloud.visibleGeometry = [];
    }

    const world = pointcloud.matrixWorld;
    const worldI = world.clone().invert();

    const pcFrustums: THREE.Frustum[] = [];
    const pcCamObjPositions: THREE.Vector3[] = [];

    for (const vc of cameras) {
      const camera = vc.camera;

      // frustum in pointcloud's object space
      const frustum = new THREE.Frustum();
      const viewI = camera.matrixWorldInverse;
      const proj = camera.projectionMatrix;
      const fm = new THREE.Matrix4().multiply(proj).multiply(viewI).multiply(world);
      frustum.setFromProjectionMatrix(fm);
      pcFrustums.push(frustum);

      // camera position in object space
      const view = camera.matrixWorld;
      const camMatrixObject = new THREE.Matrix4().multiply(worldI).multiply(view);
      const camObjPos = new THREE.Vector3().setFromMatrixPosition(camMatrixObject);
      pcCamObjPositions.push(camObjPos);
    }

    frustumsByPC.push(pcFrustums);
    camObjPositionsByPC.push(pcCamObjPositions);

    if (pointcloud.visible && pointcloud.root !== null) {
      priorityQueue.push({ pointcloud: i, node: pointcloud.root, weight: Number.MAX_VALUE });
    }

    if (resetState) {
      // hide all previously visible nodes
      if (pointcloud.root.isTreeNode()) {
        pointcloud.hideDescendants(pointcloud.root.sceneNode);
      }

      for (let j = 0; j < pointcloud.boundingBoxNodes.length; j++) {
        pointcloud.boundingBoxNodes[j].visible = false;
      }
    }
  }

  return {
    frustumsByPC,
    camObjPositionsByPC,
    priorityQueue,
  };
}

// Granular profiling for updateVisibility - exposed globally for debug widget.
// Counters accumulate across all visibility passes within a single
// updatePointClouds frame (e.g. primary + widget). Use resetVisibilityProfile
// at the start of each frame to clear the totals.
export const updateVisibilityProfile = {
  structures: 0,
  transformCheck: 0,
  mainLoop: 0,
  toTreeNode: 0,
  demUpdate: 0,
  nodeLoad: 0,
  loopIterations: 0,
  // Inside-loop breakdown (accumulated across all iterations)
  loopGetBoundingBox: 0,
  loopFrustumTest: 0,
  loopIsClipped: 0,
  loopGetChildren: 0,
  loopChildWeight: 0,
};

function resetVisibilityProfile() {
  updateVisibilityProfile.structures = 0;
  updateVisibilityProfile.transformCheck = 0;
  updateVisibilityProfile.mainLoop = 0;
  updateVisibilityProfile.toTreeNode = 0;
  updateVisibilityProfile.demUpdate = 0;
  updateVisibilityProfile.nodeLoad = 0;
  updateVisibilityProfile.loopIterations = 0;
  updateVisibilityProfile.loopGetBoundingBox = 0;
  updateVisibilityProfile.loopFrustumTest = 0;
  updateVisibilityProfile.loopIsClipped = 0;
  updateVisibilityProfile.loopGetChildren = 0;
  updateVisibilityProfile.loopChildWeight = 0;
}

/**
 * State shared across multiple visibility passes within a single frame.
 *
 * `unloadedGeometry` and `loadedToGPUThisFrame` MUST be shared so per-frame
 * load and GPU-upload caps (maxNodesLoading, 2 GPU uploads) apply across all
 * passes — otherwise a widget pass effectively doubles the budget.
 */
export interface VisibilityPassState {
  unloadedGeometry: Node[];
  loadedToGPUThisFrame: { value: number };
  lowestSpacing: { value: number };
}

export function createVisibilityPassState(): VisibilityPassState {
  return {
    unloadedGeometry: [],
    loadedToGPUThisFrame: { value: 0 },
    lowestSpacing: { value: Infinity },
  };
}

export function updateVisibility(
  pointclouds: PointCloud[],
  cameras: VisibilityCamera[],
  renderer: THREE.WebGLRenderer,
  options: { resetState?: boolean; extraBudget?: number; passState?: VisibilityPassState } = {},
) {
  const { resetState = true, extraBudget, passState = createVisibilityPassState() } = options;
  const visibleGeometry = [];

  // calculate object space frustums + cam pos per camera, setup priority queue
  const structuresStart = performance.now();
  const s = updateVisibilityStructures(pointclouds, cameras, resetState);
  updateVisibilityProfile.structures += performance.now() - structuresStart;

  // Per-pointcloud point budget cap for this pass. For the primary pass this
  // is the global pointBudget. For a widget-only follow-up pass, it's
  // numVisiblePoints (from primary) + extraBudget, so widgets can only push
  // up to extraBudget more points beyond what primary already used. The
  // pointcloud's per-cloud pointBudget is still respected on top of this.
  const passBudgetByPC = new Map<PointCloud, number>();
  for (const pc of pointclouds) {
    passBudgetByPC.set(pc, extraBudget !== undefined ? pc.numVisiblePoints + extraBudget : pointBudget);
  }

  // LOD focus region transformed into each cloud's object space, where node
  // boxes and frustums live (Box3.applyMatrix4 expands over the 8 corners,
  // so a rotated cloud yields a conservative AABB — over-inclusion only).
  const focusBoxByPC = new Map<PointCloud, THREE.Box3>();
  if (lodFocusBox) {
    for (const pc of pointclouds) {
      focusBoxByPC.set(pc, lodFocusBox.clone().applyMatrix4(pc.matrixWorld.clone().invert()));
    }
  }
  const frustumsByPC = s.frustumsByPC;
  const camObjPositionsByPC = s.camObjPositionsByPC;
  const priorityQueue = s.priorityQueue;

  // check if pointcloud has been transformed
  // some code will only be executed if changes have been detected
  const transformCheckStart = performance.now();
  const pointcloudTransformVersion = _pointcloudTransformVersion;
  for (const pointcloud of pointclouds) {
    if (!pointcloud.visible) {
      continue;
    }

    pointcloud.updateMatrixWorld();

    if (!pointcloudTransformVersion.has(pointcloud)) {
      pointcloudTransformVersion.set(pointcloud, { number: 0, transform: pointcloud.matrixWorld.clone() });
    } else {
      const version = pointcloudTransformVersion.get(pointcloud);
      if (!version) continue;

      if (!version.transform.equals(pointcloud.matrixWorld)) {
        version.number++;
        version.transform.copy(pointcloud.matrixWorld);

        pointcloud.dispatchEvent({
          type: 'transformation_changed',
          target: pointcloud,
        });
      }
    }
  }
  updateVisibilityProfile.transformCheck += performance.now() - transformCheckStart;

  const mainLoopStart = performance.now();
  let loopIterations = 0;
  let toTreeNodeTime = 0;
  // Reset inside-loop accumulators
  let loopGetBoundingBoxTime = 0;
  let loopFrustumTestTime = 0;
  let loopIsClippedTime = 0;
  let loopGetChildrenTime = 0;
  let loopChildWeightTime = 0;

  while (priorityQueue.size() > 0) {
    loopIterations++;
    const element = priorityQueue.pop();
    let node = element.node;
    const parent = element.parent;
    const pointcloud = pointclouds[element.pointcloud];

    // { // restrict to certain nodes for debugging
    //	let allowedNodes = ["r", "r0", "r4"];
    //	if(!allowedNodes.includes(node.name)){
    //		continue;
    //	}
    // }

    // Second-pass fast path: if this node was already marked visible by the
    // primary pass, the widget can free-ride on it. Skip frustum/clip/budget
    // checks entirely (primary already vouched for it) and just descend into
    // children so widget-only deeper detail can be discovered.
    if (!resetState && node.isTreeNode() && node.sceneNode.visible) {
      const pcCamObjPositionsFP = camObjPositionsByPC[element.pointcloud];
      const childrenFPStart = performance.now();
      const childrenFP = node.getChildren();
      loopGetChildrenTime += performance.now() - childrenFPStart;
      const childWeightFPStart = performance.now();
      enqueueChildrenByWeight(childrenFP, element.pointcloud, node, pointcloud, cameras, pcCamObjPositionsFP, priorityQueue, focusBoxByPC.get(pointcloud));
      loopChildWeightTime += performance.now() - childWeightFPStart;
      continue;
    }

    const getBBStart = performance.now();
    const nodeBox = node.getBoundingBox();
    loopGetBoundingBoxTime += performance.now() - getBBStart;

    const pcFrustums = frustumsByPC[element.pointcloud];
    const pcCamObjPositions = camObjPositionsByPC[element.pointcloud];

    const frustumStart = performance.now();
    // A node is visible if any camera's frustum sees it.
    let insideFrustum = false;
    for (let f = 0; f < pcFrustums.length; f++) {
      if (pcFrustums[f].intersectsBox(nodeBox)) {
        insideFrustum = true;
        break;
      }
    }
    loopFrustumTestTime += performance.now() - frustumStart;

    const maxLevel = pointcloud.maxLevel || Infinity;
    const level = node.getLevel();
    // A node in the LOD focus region is traversed no matter where the
    // camera looks — the focused load must reach areas outside the frustum.
    const inFocusRegion = focusBoxByPC.get(pointcloud)?.intersectsBox(nodeBox) === true;
    let visible = insideFrustum || inFocusRegion;
    const numberOfPointsOriginalNode = node.getNumPoints();

    const passBudget = passBudgetByPC.get(pointcloud) ?? pointBudget;
    visible = visible && pointcloud.numVisiblePoints + numberOfPointsOriginalNode <= passBudget;
    visible = visible && pointcloud.numVisiblePoints + numberOfPointsOriginalNode <= pointcloud.pointBudget;
    visible = visible && level < maxLevel;
    visible = visible && level <= pointcloud.downSamplingLevel;
    // visible = visible || node.getLevel() <= 2;

    const clipStart = performance.now();
    const clipped = isNodeClipped(pointcloud, nodeBox);
    loopIsClippedTime += performance.now() - clipStart;
    if (clipped) visible = false;

    if (node.spacing) {
      passState.lowestSpacing.value = Math.min(passState.lowestSpacing.value, node.spacing);
    } else if (node.geometryNode && node.geometryNode.spacing) {
      passState.lowestSpacing.value = Math.min(passState.lowestSpacing.value, node.geometryNode.spacing);
    }

    if (pointcloud.numVisiblePoints + numberOfPointsOriginalNode > passBudget) {
      break;
    }

    if (!visible) {
      if (parent !== undefined) {
        parent.numInvisibleChildren++;
        optionalSkipNode(pointcloud, parent);
      }

      continue;
    }

    adjustVisiblePoints(pointcloud, numberOfPointsOriginalNode);

    pointcloud.numVisibleNodes++;

    if (node.isGeometryNode() && (!parent || parent.isTreeNode())) {
      if (node.isLoaded() && passState.loadedToGPUThisFrame.value < 2) {
        const toTreeStart = performance.now();
        node = pointcloud.toTreeNode(node, parent);
        toTreeNodeTime += performance.now() - toTreeStart;
        passState.loadedToGPUThisFrame.value++;
      } else {
        passState.unloadedGeometry.push(node);
        visibleGeometry.push(node);
      }
    }

    if (node.isTreeNode()) {
      node.sceneNode.visible = true;
      node.sceneNode.material = pointcloud.material;

      // Add node to LRU cache for memory management
      // This enables freeMemory() to evict old nodes when memory limit is exceeded
      if (node.geometryNode) {
        lru()?.touch(node.geometryNode as Parameters<NonNullable<ReturnType<typeof lru>>['touch']>[0]);
      }

      pointcloud.visibleNodes.push(node);

      if (parent !== undefined) {
        parent.numVisibleChildren++;
        optionalSkipNode(pointcloud, parent);
      }

      if (node._transformVersion === undefined) {
        node._transformVersion = -1;
      }
      const transformVersion = pointcloudTransformVersion.get(pointcloud);
      if (transformVersion && node._transformVersion !== transformVersion.number) {
        node.sceneNode.updateMatrix();
        node.sceneNode.matrixWorld.multiplyMatrices(pointcloud.matrixWorld, node.sceneNode.matrix);
        node._transformVersion = transformVersion.number;
      }

      if (pointcloud.showBoundingBox && !node.boundingBoxNode && node.getBoundingBox) {
        const boxHelper = new Box3Helper(node.getBoundingBox());
        boxHelper.matrixAutoUpdate = false;
        pointcloud.boundingBoxNodes.push(boxHelper);
        node.boundingBoxNode = boxHelper;
        node.boundingBoxNode.matrix.copy(pointcloud.matrixWorld);
      } else if (pointcloud.showBoundingBox && node.boundingBoxNode) {
        node.boundingBoxNode.visible = true;
        node.boundingBoxNode.matrix.copy(pointcloud.matrixWorld);
      } else if (!pointcloud.showBoundingBox && node.boundingBoxNode) {
        node.boundingBoxNode.visible = false;
      }
    }

    // add child nodes to priorityQueue
    if (node.skipDuplicatePointsPythagoras) {
      node.refinedByAllChildren = false;
    }
    const getChildrenStart = performance.now();
    const children = node.getChildren();
    loopGetChildrenTime += performance.now() - getChildrenStart;

    const childWeightStart = performance.now();
    enqueueChildrenByWeight(children, element.pointcloud, node, pointcloud, cameras, pcCamObjPositions, priorityQueue, focusBoxByPC.get(pointcloud));
    loopChildWeightTime += performance.now() - childWeightStart;
  } // end priority queue loop
  updateVisibilityProfile.mainLoop += performance.now() - mainLoopStart;
  updateVisibilityProfile.toTreeNode += toTreeNodeTime;
  updateVisibilityProfile.loopIterations += loopIterations;
  updateVisibilityProfile.loopGetBoundingBox += loopGetBoundingBoxTime;
  updateVisibilityProfile.loopFrustumTest += loopFrustumTestTime;
  updateVisibilityProfile.loopIsClipped += loopIsClippedTime;
  updateVisibilityProfile.loopGetChildren += loopGetChildrenTime;
  updateVisibilityProfile.loopChildWeight += loopChildWeightTime;

  const demUpdateStart = performance.now();
  {
    // update DEM
    const maxDEMLevel = 4;
    const candidates = pointclouds.filter((p) => p.generateDEM && p.dem);
    for (const pointcloud of candidates) {
      const updatingNodes = pointcloud.visibleNodes.filter((n) => n.getLevel() <= maxDEMLevel);
      pointcloud.dem?.update(updatingNodes);
    }
  }
  updateVisibilityProfile.demUpdate += performance.now() - demUpdateStart;

  if (
    Object.values(loaded_tiles_network).length &&
    Object.values(loaded_tiles_network).every((value) => !!value) &&
    Object.values(rendered_tiles).every((value) => !!value) &&
    Object.values(loaded_tiles_network).length === Object.values(rendered_tiles).length
  ) {
    if (!endRender) {
      endRender = performance.now();
      (stats as Record<string, unknown>).loadTime = `${(endRender - stats.startRender) / 1000}s`;

      //stats.decodings.forEach(decoding => {
      //  if (decoding.time < 5000)
      //    console.log(`Decoder:: decompression took ${decoding.time}ms for file ${decoding.fileUrl}`);
      //  else console.warn(`Decoder:: decompression took ${decoding.time}ms for file ${decoding.fileUrl}`);
      //});
      //console.log('total load time', (stats as Record<string, unknown>).loadTime);
      resetStats();
    }
  }

  // lowestSpacing accumulates across passes via passState. The returned value
  // reflects only THIS pass's lowestSpacing accumulation, but since passState
  // is shared, the caller can read passState.lowestSpacing.value for the
  // cross-pass minimum — we expose it via the return value of the primary
  // pass (which is what updatePointClouds returns to viewer.ts).
  return {
    lowestSpacing: passState.lowestSpacing.value,
  };
}

function optionalSkipNode(pointcloud: PointCloud, node: Node) {
  if (isNodeRefinedByAllChildren(pointcloud, node)) {
    pointcloud.numRefinedParents++;
    pointcloud.numSkippedPoints += node.getNumPoints();
    node.refinedByAllChildren = true;
    adjustVisiblePoints(pointcloud, -node.getNumPoints());
  }
}

function isNodeRefinedByAllChildren(pointcloud: PointCloud, parent: Node) {
  if (pointcloud.pcoGeometry.type === 'ppc') {
    return parent.numCandidateChildren === parent.numVisibleChildren + parent.numInvisibleChildren;
  }
  return false;
}

function adjustVisiblePoints(pointcloud: PointCloud, count: number) {
  pointcloud.numVisiblePoints += count;
}

/**
 * Compute each child's max screen-pixel weight across the supplied cameras and
 * push the children that any camera sees big enough into the priority queue.
 * Extracted so the visibility loop's fast path (already-visible-from-primary)
 * can descend without re-running the inline weight computation by hand.
 */
function enqueueChildrenByWeight(
  children: Node[],
  pointcloudIndex: number,
  parent: Node,
  pointcloud: PointCloud,
  cameras: VisibilityCamera[],
  pcCamObjPositions: THREE.Vector3[],
  priorityQueue: BinaryHeap<PriorityQueueElement>,
  focusBox?: THREE.Box3,
) {
  for (let i = 0; i < children.length; i++) {
    const child = children[i];

    // Compute weight per camera; take the max so the most-zoomed-in view
    // drives selection. A child below minimumNodePixelSize in every camera
    // is skipped; if any camera sees it big enough, it stays in.
    let bestWeight = 0;
    let anyCameraSeesItBigEnough = false;
    const sphere = child.getBoundingSphere();
    const center = sphere.center;
    const radius = sphere.radius;

    for (let c = 0; c < cameras.length; c++) {
      const cam = cameras[c].camera;
      const camDomHeight = cameras[c].domHeight;
      const camObjPos = pcCamObjPositions[c];

      let weight = 0;
      if ((cam as THREE.PerspectiveCamera).isPerspectiveCamera) {
        const dx = camObjPos.x - center.x;
        const dy = camObjPos.y - center.y;
        const dz = camObjPos.z - center.z;
        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

        const fov = ((cam as THREE.PerspectiveCamera).fov * Math.PI) / 180;
        const slope = Math.tan(fov / 2);
        const projFactor = (0.5 * camDomHeight) / (slope * distance);
        const screenPixelRadius = radius * projFactor;

        if (screenPixelRadius < pointcloud.minimumNodePixelSize) continue;
        anyCameraSeesItBigEnough = true;

        weight = screenPixelRadius;
        if (distance - radius < 0) weight = Number.MAX_VALUE;
      } else {
        // Orthographic: weight by bounding-box diagonal as before.
        // Doesn't gate on minimumNodePixelSize (matches legacy behavior).
        const bb = child.getBoundingBox();
        const diagonal = bb.max.clone().sub(bb.min).length();
        weight = diagonal;
        anyCameraSeesItBigEnough = true;
      }

      if (weight > bestWeight) bestWeight = weight;
    }

    // LOD focus region: refine regardless of camera distance/direction.
    // Object-space radius as weight keeps the descent coarse-first; the
    // point budget is the only cutoff.
    if (focusBox && focusBox.intersectsSphere(sphere)) {
      anyCameraSeesItBigEnough = true;
      bestWeight = Math.max(bestWeight, radius);
    }

    if (!anyCameraSeesItBigEnough) continue;

    // if (child.preLoad) child.preLoad();
    priorityQueue.push({ pointcloud: pointcloudIndex, node: child, parent, weight: bestWeight });
  }
}

function isNodeClipped(pointcloud: PointCloud, nodeBox: THREE.Box3): boolean {
  let clip = false;
  let isolateAnything = false;
  let isolateThis = false;

  const clipBoxes = pointcloud.material.clipBoxes;
  if (clipBoxes.length > 0) {
    for (const clipBox of clipBoxes) {
      const pcWorldInverse = pointcloud.matrixWorld.clone().invert();
      pcWorldInverse.multiply(clipBox.box.matrixWorld);

      const px = new THREE.Vector3(+0.5, 0, 0).applyMatrix4(pcWorldInverse);
      const nx = new THREE.Vector3(-0.5, 0, 0).applyMatrix4(pcWorldInverse);
      const py = new THREE.Vector3(0, +0.5, 0).applyMatrix4(pcWorldInverse);
      const ny = new THREE.Vector3(0, -0.5, 0).applyMatrix4(pcWorldInverse);
      const pz = new THREE.Vector3(0, 0, +0.5).applyMatrix4(pcWorldInverse);
      const nz = new THREE.Vector3(0, 0, -0.5).applyMatrix4(pcWorldInverse);

      const pxN = new THREE.Vector3().subVectors(nx, px).normalize();
      const nxN = pxN.clone().multiplyScalar(-1);
      const pyN = new THREE.Vector3().subVectors(ny, py).normalize();
      const nyN = pyN.clone().multiplyScalar(-1);
      const pzN = new THREE.Vector3().subVectors(nz, pz).normalize();
      const nzN = pzN.clone().multiplyScalar(-1);

      const pxPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(pxN, px);
      const nxPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(nxN, nx);
      const pyPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(pyN, py);
      const nyPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(nyN, ny);
      const pzPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(pzN, pz);
      const nzPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(nzN, nz);

      const clippingFrustum = new THREE.Frustum(pxPlane, nxPlane, pyPlane, nyPlane, pzPlane, nzPlane);
      const clipBoxContainsNode = clippingFrustum.containsPoint(nodeBox.max) && clippingFrustum.containsPoint(nodeBox.min);
      const noOverlap = !clippingFrustum.intersectsBox(nodeBox);

      if (clipBoxContainsNode) {
        if (clipBox.box.getActualClipTask() === ClipTask.SHOW_OUTSIDE) {
          clip = true;
        } else if (clipBox.box.getActualClipTask() === ClipTask.SHOW_INSIDE) {
          isolateAnything = true;
          isolateThis = true;
        }
      } else if (noOverlap) {
        if (clipBox.box.getActualClipTask() === ClipTask.SHOW_INSIDE) {
          isolateAnything = true;
        }
      } else {
        if (clipBox.box.getActualClipTask() === ClipTask.SHOW_INSIDE) {
          isolateAnything = true;
          isolateThis = true; // Maybe
        }
      }
    }
  }

  const clipPlanes = pointcloud.material.clipPlanes;
  if (clipPlanes.length > 0) {
    for (const clipPlane of clipPlanes) {
      const plane = clipPlane.plane;

      if (plane.actualClipTask === ClipTask.SHOW_INSIDE || (plane.actualClipTask === ClipTask.HEATMAP && plane.heatmapIsolate)) {
        const normal = plane.getNormal();
        const worldBox = new THREE.Box3(nodeBox.min.clone().applyMatrix4(pointcloud.matrixWorld), nodeBox.max.clone().applyMatrix4(pointcloud.matrixWorld));

        const clipBoundary1 = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, plane.position.clone().add(normal.clone().multiplyScalar(plane.tolerance)));

        const clipBoundary2 = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, plane.position.clone().add(normal.clone().multiplyScalar(-plane.tolerance)));

        const overlap = clipBoundary1.intersectsBox(worldBox) || clipBoundary2.intersectsBox(worldBox) || plane.pointInsideClipArea(worldBox.min);

        isolateAnything = true;
        if (overlap) {
          isolateThis = true;
        }
      }
    }
  }

  // NOTE: this return used to sit INSIDE the clip-planes block, so with clip
  // BOXES but no clip planes the function returned undefined and box-based
  // node culling never happened — SHOW_INSIDE boxes were shader-only and the
  // LOD budget kept refining the hidden rest of the scene. Nodes partially
  // overlapping a SHOW_INSIDE box count as inside (kept), so boundary and
  // ancestor nodes always survive.
  return (isolateAnything && !isolateThis) || clip;
}
